import { ApplicationConfig, APP_INITIALIZER, importProvidersFrom, inject, provideAppInitializer, provideZonelessChangeDetection } from '@angular/core'
import { provideRouter } from '@angular/router'
import { provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http'
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async'
import { provideOptimus } from '@openng/optimus-ui/config'
import { MessageService, ConfirmationService } from '@openng/optimus-ui/api'
import { provideEnvironmentNgxMask } from 'ngx-mask'
import { NgxUiLoaderModule, NgxUiLoaderConfig, NgxUiLoaderHttpModule, NgxUiLoaderRouterModule } from 'ngx-ui-loader'
import { provideEchartsCore } from 'ngx-echarts'
import { echarts } from './_shared/echarts-setup'
import { SocketIoModule, SocketIoConfig } from 'ngx-socket-io'
import { MsalModule, MsalService } from '@azure/msal-angular'
import { InteractionType, PublicClientApplication } from '@azure/msal-browser'
import { FaIconLibrary } from '@fortawesome/angular-fontawesome'
import { FA_ICONS } from './_shared/fa-icons'
import { routes } from './app.routes'
import { MaintenanceService } from './_shared/maintenance/maintenance.service'
import { tokenInterceptor } from './FuseQ/fuseq-interceptors/fuseq.token.interceptor'
import { errorInterceptor } from './FuseQ/fuseq-interceptors/fuseq.error.interceptor'
import { environment } from '../environments/environment'
import FuseqTheme from './fuseqTheme'

const socketConfig: SocketIoConfig = {
  url: environment.API_URL_RAIDEN,
  options: {
    transports: ['websocket'],
    path: '/raiden',
    // Il server disconnette i client privi di user_id. Non connettersi
    // automaticamente: la connessione viene aperta solo da updateUserId()
    // una volta noto l'utente, evitando il loop di riconnessioni.
    autoConnect: false,
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000
  }
}

export function msalRedirectInitializer (msal: MsalService) {
  return () => msal.handleRedirectObservable()
}

export function faIconsInitializer (faIconLibrary: FaIconLibrary) {
  return () => faIconLibrary.addIconPacks(FA_ICONS)
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    provideRouter(routes),
    provideHttpClient(withInterceptors([tokenInterceptor, errorInterceptor]), withInterceptorsFromDi()),
    provideAnimationsAsync(),
    MessageService,
    ConfirmationService,
    provideEnvironmentNgxMask(),
    importProvidersFrom(NgxUiLoaderModule.forRoot({
      bgsColor: '#19549f',
      bgsOpacity: 1,
      bgsPosition: 'bottom-right',
      bgsSize: 60,
      bgsType: 'three-strings',
      blur: 15,
      fgsColor: '#19549f',
      fgsPosition: 'center-center',
      fgsSize: 60,
      fgsType: 'square-jelly-box',
      gap: 100,
      logoPosition: 'top-center',
      logoSize: 300,
      // "logoUrl": "../assets/img/brand/posteLoading.png",
      masterLoaderId: 'master',
      overlayBorderRadius: '0',
      overlayColor: 'rgba(100, 100, 100, 0.9)',
      pbColor: '#19549f',
      pbDirection: 'ltr',
      pbThickness: 3,
      hasProgressBar: true
    } as NgxUiLoaderConfig)),
    // `exclude` fa `url.startsWith(...)`, quindi con URL assoluti non matcha:
    // per il poll di maintenance serve `excludeRegexp` (test sulla URL completa).
    // Il match esclude solo l'endpoint di stato `/maintenance` (con eventuale
    // query), non `/maintenance/reload`.
    importProvidersFrom(NgxUiLoaderHttpModule.forRoot({ showForeground: false, excludeRegexp: ['/maintenance(?:\\?.*)?$'] })),
    importProvidersFrom(NgxUiLoaderRouterModule.forRoot({ showForeground: true })),
    provideEchartsCore({ echarts }),
    importProvidersFrom(SocketIoModule.forRoot(socketConfig)),
    importProvidersFrom(MsalModule.forRoot(
      new PublicClientApplication({
        auth: {
          clientId: environment.CLIENT_ID,
          authority: 'https://login.microsoftonline.com/' + environment.TENANT_ID,
          redirectUri: environment.REDIRECT_URI
        },
        cache: {
          cacheLocation: 'localStorage'
        }
      }),
      {
        interactionType: InteractionType.Popup,
        authRequest: {
          scopes: ['user.read']
        }
      },
      {
        interactionType: InteractionType.Redirect,
        protectedResourceMap: new Map([
          ['https://graph.microsoft.com/v1.0/me', ['user.read']]
        ])
      }
    )),
    { provide: APP_INITIALIZER, useFactory: msalRedirectInitializer, deps: [MsalService], multi: true },
    { provide: APP_INITIALIZER, useFactory: faIconsInitializer, deps: [FaIconLibrary], multi: true },
    provideAppInitializer(() => {
      inject(MaintenanceService).start()
    }),
    provideOptimus({
      theme: {
        preset: FuseqTheme,
        options: {
          darkModeSelector: '.fq-dark-mode'
        }
      }
    })
  ]
}
